// nflverse stats_player_week_YYYY.csv → `stat.line` facts.
//
// What a player actually did, one row per player per week. Everything a manager
// weighs after "can he play" is downstream of this file: recent form, season
// pace, any projection, every feature in Phase 9, and an opponent's profile
// against a position.
//
// THE ASSET IS `stats_player_week`, NOT `stats_player`. The release carries four
// variants per season and only one of them is period-grained:
//
//   stats_player_week_YYYY      8.1 MB   one row per player per week  ← this
//   stats_player_reg_YYYY       0.8 MB   regular-season totals
//   stats_player_post_YYYY      0.2 MB   postseason totals
//   stats_player_regpost_YYYY            both, combined
//
// The season totals are derivable from the weeks and the weeks are not
// derivable from the totals, so the weekly file is the only one worth storing.
//
// KEYS ARE OURS. `packages/sports-football/src/stats.ts` says it in its own
// header — "Keys are ours, not the provider's; the adapter maps provider fields
// onto these at the boundary" — and this is that boundary. The target keys are
// written as string literals rather than imported because `@fsp/sports-football`
// depends on `@fsp/info` and the import would be a cycle; `vocabulary.ts`
// already carries football's concept ids the same way, for the same reason.
//
// WHAT THIS FILE CANNOT SAY, recorded here rather than rediscovered:
//
//   snap_pct         Snap counts are Pro-Football-Reference's, `commercial_use: 0`.
//                    Free-first rules them out and that is a priced absence,
//                    not a to-do. See DATA_SOURCES.md.
//   dst_pts_allowed  Team-defense scoring is a TEAM line, and this file is per
//   dst_yds_allowed  player. A DST fact needs a different grain and a different
//   dst_ret_td       source row; it is not hiding in here under another name.
//   rz_touches       Not published in this asset at any name.
//   opp_rank_vs_pos  Ours to compute, once there are enough weeks to compute it
//                    over. That is the feature layer, Phase 9.
//
// Emitting a zero for any of those would be the worst available outcome: a
// scoring rule would silently score nothing and look like it worked.

import { cell, num, parseCsv } from '../../format/csv';
import type {
  CandidateEntity,
  CandidateFact,
  ExtractResult,
  UnmappedValue,
} from '../../acquisition/adapter';
import { NS } from './namespaces';

/** Columns without which a row cannot be placed. Every one has been stable since 1999. */
const REQUIRED = ['player_id', 'season', 'week', 'season_type', 'team'] as const;

/**
 * Our stat key ← their column.
 *
 * One entry per key we can actually fill. A key absent from this table is
 * absent from the payload, and a consumer asking for it gets `undefined`
 * rather than `0` — see the header for which ones and why.
 */
const DIRECT: readonly (readonly [ourKey: string, column: string])[] = [
  // Passing
  ['pass_att', 'attempts'],
  ['pass_cmp', 'completions'],
  ['pass_yd', 'passing_yards'],
  ['pass_td', 'passing_tds'],
  ['pass_int', 'passing_interceptions'],
  ['pass_2pt', 'passing_2pt_conversions'],
  ['pass_sack', 'sacks_suffered'],
  ['pass_fd', 'passing_first_downs'],
  // Rushing
  ['rush_att', 'carries'],
  ['rush_yd', 'rushing_yards'],
  ['rush_td', 'rushing_tds'],
  ['rush_2pt', 'rushing_2pt_conversions'],
  ['rush_fd', 'rushing_first_downs'],
  // Receiving
  ['rec_tgt', 'targets'],
  ['rec', 'receptions'],
  ['rec_yd', 'receiving_yards'],
  ['rec_td', 'receiving_tds'],
  ['rec_2pt', 'receiving_2pt_conversions'],
  ['rec_fd', 'receiving_first_downs'],
  ['air_yd', 'receiving_air_yards'],
  ['rec_yac', 'receiving_yards_after_catch'],
  ['target_share', 'target_share'],
  // Fumbles
  ['fum', 'fumbles_total'],
  ['fum_lost', 'fumbles_lost_total'],
  ['fum_rec_td', 'fumble_recovery_tds'],
  // Kicking
  ['fg_0_19', 'fg_made_0_19'],
  ['fg_20_29', 'fg_made_20_29'],
  ['fg_30_39', 'fg_made_30_39'],
  ['fg_40_49', 'fg_made_40_49'],
  ['fg_miss', 'fg_missed'],
  ['xp_made', 'pat_made'],
  ['xp_miss', 'pat_missed'],
  // A PERSON'S OWN DEFENSE, as `idp_*` (Q11, PLAN-2026-10-18-defensive-players
  // §3.2). Until 2026-10-18 these columns went onto every person's line as the
  // TEAM defense's keys (`dst_sack`, `dst_int`, …), and every preset scores
  // those with no position named, so an offensive player who forced or
  // recovered a fumble on special teams scored team-defense points. A team
  // defense's line is its club's (`team-stats.ts`) and never a person's.
  ['idp_tkl_solo', 'def_tackles_solo'],
  ['idp_tkl_loss', 'def_tackles_for_loss'],
  ['idp_sack', 'def_sacks'],
  ['idp_qb_hit', 'def_qb_hits'],
  ['idp_int', 'def_interceptions'],
  ['idp_pass_def', 'def_pass_defended'],
  ['idp_ff', 'def_fumbles_forced'],
  ['idp_fum_rec', 'fumble_recovery_opp'],
  ['idp_td', 'def_tds'],
  ['idp_safety', 'def_safeties'],
];

/** nflverse's position groups for defensive players. */
const DEFENSIVE_GROUPS: ReadonlySet<string> = new Set(['DL', 'LB', 'DB']);

/** Keys that are a sum of several columns rather than one. */
const SUMMED: readonly (readonly [ourKey: string, columns: readonly string[]])[] = [
  // Our catalog stops at 50+; theirs splits 50-59 and 60+.
  ['fg_50p', ['fg_made_50_59', 'fg_made_60_']],
  ['idp_blk', ['def_punt_blocks', 'def_pat_blocks', 'def_fg_blocks']],
  // A tackle shared with a teammate is an assisted tackle, whichever of the
  // two the play-by-play credited it to: the three tackle columns sum to a
  // player's total (Bobby Wagner, 2025: 75 + 4 + 83 = 162).
  ['idp_tkl_ast', ['def_tackles_with_assist', 'def_tackle_assists']],
  ['ret_yd', ['punt_return_yards', 'kickoff_return_yards']],
];

/**
 * The source's fantasy totals, as a list of the schemes it actually priced.
 *
 * A blank column is dropped rather than carried as a null, because the list is
 * the answer to "which schemes did the source price this line under" and a
 * null entry answers it twice. `[]` is a perfectly good line — a defensive
 * player in a file that prices no defensive scoring — and is distinguishable
 * from a missing key, which the registry still requires.
 */
function scores(priced: Record<string, number | null>): { scheme: string; value: number }[] {
  return Object.entries(priced)
    .filter((e): e is [string, number] => e[1] !== null)
    .map(([scheme, value]) => ({ scheme, value }));
}

export interface StatLineOptions {
  observedAt: string;
  vocabulary?: Record<string, ReadonlySet<string>>;
}

/**
 * Every column a line is scored from. A blank cell is "not recorded"; a column
 * gone from the header is a renamed or dropped one, and read as absent it
 * scores that stat 0 for every league with no alarm (the review of
 * 2026-10-31). So it fails the run as drift, as a missing key column does.
 * Checked against every weekly file from 2020 to 2026 on 2026-10-31.
 */
const SCORED: readonly string[] = [
  ...DIRECT.map(([, column]) => column),
  ...SUMMED.flatMap(([, columns]) => columns),
  'special_teams_tds',
  // Which group a player is in decides where his fumble-return touchdown goes.
  'position_group',
];

export function extractStatLines(text: string, opts: StatLineOptions): ExtractResult {
  const table = parseCsv(text);

  for (const column of REQUIRED) {
    if (!table.header.includes(column)) {
      throw new Error(
        `nflverse stats: required column "${column}" is missing. ` +
          `Header has ${table.header.length} columns: ${table.header.join(', ')}`,
      );
    }
  }
  const gone = SCORED.filter((column) => !table.header.includes(column));
  if (gone.length > 0)
    throw new Error(
      `nflverse stats: ${gone.length === 1 ? 'a column' : 'columns'} the scoring reads ${gone.length === 1 ? 'is' : 'are'} missing: ${gone.join(', ')}. ` +
        `Header has ${table.header.length} columns: ${table.header.join(', ')}`,
    );

  const entities = new Map<string, CandidateEntity>();
  const facts: CandidateFact[] = [];
  const unmapped = new Map<string, UnmappedValue>();
  const skipped = new Map<string, number>();
  const weeks = new Set<string>();
  const emitted = new Set<string>();

  const positions = opts.vocabulary?.position;

  const skip = (reason: string) => skipped.set(reason, (skipped.get(reason) ?? 0) + 1);

  for (const row of table.rows) {
    const personKey = cell(row, 'player_id');
    const season = num(row, 'season');
    const period = num(row, 'week');
    const team = cell(row, 'team');
    const seasonType = cell(row, 'season_type');

    if (personKey === null) {
      skip('no player_id');
      continue;
    }
    if (season === null || period === null) {
      skip('no season or week');
      continue;
    }
    if (team === null) {
      skip('no team');
      continue;
    }

    // A postseason week 1 and a regular-season week 1 are different weeks and
    // must not collide. The grain carries the season type for that reason.
    const factKey = `${season}|${seasonType ?? 'REG'}|${period}|${personKey}`;
    if (emitted.has(factKey)) {
      skip('duplicate player-week in one file');
      continue;
    }
    emitted.add(factKey);
    // THE TRAILING SEPARATOR IS LOAD-BEARING. The scope is matched with
    // `startsWith` against a key of `season|type|period|person`, so a prefix of
    // `2026|REG|1` also matches every key for weeks 10 to 19 — and a weekly
    // file that no longer mentions week 17 would end thousands of week-1
    // through week-19 stat lines under the wrong scope. Rosters and injuries
    // have always written the separator; this one did not (audit 2.10).
    weeks.add(`${season}|${seasonType ?? 'REG'}|${period}|`);

    const position = cell(row, 'position');
    if (position !== null && positions !== undefined && !positions.has(position)) {
      const at = `position:${position}`;
      const seen = unmapped.get(at);
      if (seen) seen.count++;
      else unmapped.set(at, { field: 'position', value: position, count: 1, sample: personKey });
    }

    // ── The line itself ───────────────────────────────────────────────────
    //
    // ZEROS ARE OMITTED, and that is safe here for one measured reason: this
    // source is DENSE. Across the 2026 file there is not a single blank cell in
    // any stat column — a quarterback's `fg_made_0_19` is `0`, not empty — so
    // "absent" carries no information the value does not, and 95% of the values
    // are zero. Measured on that file: 5,628 keys become 298, and the payloads
    // go from 92.9 KB to 28.9 KB. Over five seasons that is about 62 MB against
    // 20 MB in D1.
    //
    // This is the narrow exception to "absent and zero are different claims",
    // not an abandonment of it. The rule protects against a source that can be
    // SILENT; this one never is. An adapter whose source publishes some stat
    // categories and not others must not emit `stat.line` without extending the
    // kind to say which categories it covered — otherwise an unpublished
    // defensive line reads as a defense that did nothing.
    const stats: Record<string, number> = {};
    for (const [ourKey, column] of DIRECT) {
      const v = num(row, column);
      if (v !== null && v !== 0) stats[ourKey] = v;
    }
    for (const [ourKey, columns] of SUMMED) {
      const parts = columns.map((c) => num(row, c)).filter((v): v is number => v !== null);
      if (parts.length === 0) continue;
      const total = parts.reduce((a, b) => a + b, 0);
      if (total !== 0) stats[ourKey] = total;
    }

    // Derived, because they publish the two halves and we score the difference.
    const att = stats['pass_att'];
    if (att !== undefined && att !== (stats['pass_cmp'] ?? 0)) {
      stats['pass_inc'] = att - (stats['pass_cmp'] ?? 0);
    }

    // `special_teams_tds` counts punt and kickoff return scores together, which
    // is exactly our `ret_td`. Kept separate from `dst_td`, which is a defense
    // scoring, because a league may price them differently.
    const stTds = num(row, 'special_teams_tds');
    if (stTds !== null && stTds !== 0) stats['ret_td'] = stTds;

    // A DEFENDER'S FUMBLE RETURNED FOR A TOUCHDOWN IS A DEFENSIVE TOUCHDOWN
    // (the review of Q11's backend). nflverse files it under
    // `fumble_recovery_tds`, which for an offensive player is his own fumble
    // recovered in the end zone (`fum_rec_td`), and its `def_tds` is all but
    // only interception returns; a defender's table has no fumble-recovery-
    // touchdown row, so his goes into his defensive touchdowns.
    if (DEFENSIVE_GROUPS.has(cell(row, 'position_group') ?? '') && stats['fum_rec_td']) {
      stats['idp_td'] = (stats['idp_td'] ?? 0) + stats['fum_rec_td'];
      delete stats['fum_rec_td'];
    }

    if (!entities.has(`${NS.person}:${personKey}`)) {
      entities.set(`${NS.person}:${personKey}`, {
        type: 'person',
        namespace: NS.person,
        key: personKey,
        display: cell(row, 'player_display_name') ?? cell(row, 'player_name') ?? personKey,
        otherKeys: {},
        evidence: { position, team, birthDate: null },
      });
    }
    if (!entities.has(`${NS.team}:${team}`)) {
      entities.set(`${NS.team}:${team}`, {
        type: 'team',
        namespace: NS.team,
        key: team,
        display: team,
        otherKeys: {},
        evidence: { league: 'nfl' },
      });
    }

    const opponent = cell(row, 'opponent_team');
    const refs: CandidateFact['refs'] = [
      { namespace: NS.person, key: personKey, role: 'subject' },
      { namespace: NS.team, key: team, role: 'employer' },
    ];
    if (opponent !== null) {
      if (!entities.has(`${NS.team}:${opponent}`)) {
        entities.set(`${NS.team}:${opponent}`, {
          type: 'team',
          namespace: NS.team,
          key: opponent,
          display: opponent,
          otherKeys: {},
          evidence: { league: 'nfl' },
        });
      }
      refs.push({ namespace: NS.team, key: opponent, role: 'participant' });
    }

    facts.push({
      kind: 'stat.line',
      key: factKey,
      // A stat line becomes true when the game is played. The file carries no
      // timestamp of its own, so the capture is the honest claim — and a
      // correction issued on Tuesday supersedes through the ordinary mechanism
      // rather than overwriting what was true on Sunday.
      validFrom: opts.observedAt,
      validTo: null,
      refs,
      topics: ['performance'],
      confidence: 'official',
      payload: {
        personKey,
        season,
        period,
        team,
        seasonType,
        position,
        opponent,
        gameId: cell(row, 'game_id'),
        stats,
        // The source's own fantasy totals, carried as a REFERENCE rather than a
        // score: every league scores by its own rules and ours are data. They
        // are here so a drifting scoring engine can be caught against a second
        // opinion, and they must never be rendered as a league's points.
        //
        // A scheme the file left blank is simply absent, rather than present
        // and null: the list says which totals the source priced, and nflverse
        // prices exactly these two. The spellings are its own, from its data
        // dictionary, and stay its own.
        sourcePoints: scores({
          standard: num(row, 'fantasy_points'),
          ppr: num(row, 'fantasy_points_ppr'),
        }),
      },
    });
  }

  return {
    entities: [...entities.values()],
    facts,
    unmapped: [...unmapped.values()],
    // A week's file is the whole of that week. Somebody we hold a line for who
    // is no longer in it did not play after all — a correction, not a memory.
    complete: [...weeks].map((prefix) => ({ kind: 'stat.line', prefix })),
    skipped: [...skipped.entries()].map(([reason, count]) => ({ reason, count })),
  };
}
